import { httpApi } from '@api/http.api';
import { CONTENT_TYPE_FORM_DATA_HEADER, NO_REQUEST_TIMEOUT } from '@constants/api';
import type {
    CommonTablePaginatedResponse,
    CommonTableRequest,
} from '@routes/sidebar/admin-center/___shared___/types.ts';
import type { CSV_TYPE } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___shared___/constants.ts';
import type { CsvUploadStateResponse as SharedCsvUploadStateResponse } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___shared___/types.ts';
import type { AxiosResponse } from 'axios';

export type VesselsRequest = CommonTableRequest;

export interface CommonVesselProps {
    name: string | null;
    imoNumber: string | null;

    flagId: number | null;
    vesselSubTypeId: number | null;
    vesselGroupId: number | null;

    vesselEmail: string | null;

    isActive: boolean;

    grt: number | null;
    enginePowerKW: number | null;

    engineDetailsId: number | null;
    ecdisDetailsId: number | null;
    bwmDetailsId: number | null;
    scrubberDetailsId: number | null;
    highVoltageDetailsId: number | null;
    radarDetailsId: number | null;
    craneDetailsId: number | null;
    lifeboatDavitsDetailsId: number | null;

    technicalShoreTeamId: number | null;
    crewingShoreTeamId: number | null;
    vesselSuperintendentId: number | null;

    subscriptionStartDate: string | null;
    subscriptionEndDate: string | null;
    autoRenewal: boolean;

    fuelTypeIds: number[];
    tradingAreaIds: number[];
}

export interface Vessel extends CommonVesselProps {
    id: number;
    flagJurisdiction: string | null;
    flagCodeWithJurisdiction: string | null;
    vesselSubType: string | null;
    vesselSubTypeShortCode: string | null;
    vesselTypeWithSubtype: string | null;
    vesselGroupName: string | null;
    technicalShoreTeamName: string | null;
    crewingShoreTeamName: string | null;
    vesselSuperintendentName: string | null;
    createdAtUTC: string | null;
    updatedAtUTC: string | null;

    //new data
    engineDetailsName?: string | null;
    ecdisDetailsName?: string | null;
    bwmDetailsName?: string | null;
    scrubberDetailsName?: string | null;
    highVoltageDetailsName?: string | null;
    radarDetailsName?: string | null;
    craneDetailsName?: string | null;
    lifeboatDavitsDetailsName?: string | null;
}

export interface VesselsResultsDescription {
    [k: string]: unknown;
}

export interface CsvUploadStateResponse extends SharedCsvUploadStateResponse {
    csvType: typeof CSV_TYPE.VESSEL;
}

export interface VesselsResponse {
    data: Vessel[];
    pagination: CommonTablePaginatedResponse;
    resultsDescription: VesselsResultsDescription;
}

export type VesselPayload = CommonVesselProps;

export type CreateVesselRequest = VesselPayload;
export type UpdateVesselRequest = VesselPayload;

export const getApiVessels = (
    clientId: number,
    tenantAlias: string,
    payload: VesselsRequest,
): Promise<VesselsResponse> =>
    httpApi
        .post<VesselsResponse>(`admin-center/clients/${clientId}/tenants/${tenantAlias}/vessels/search`, payload)
        .then(({ data }) => data);

export const createApiVessel = (clientId: number, tenantAlias: string, payload: CreateVesselRequest): Promise<Vessel> =>
    httpApi
        .post<Vessel>(`admin-center/clients/${clientId}/tenants/${tenantAlias}/vessels`, payload)
        .then(({ data }) => data);

export const updateApiVessel = (
    clientId: number,
    tenantAlias: string,
    id: number,
    payload: UpdateVesselRequest,
): Promise<Vessel> =>
    httpApi
        .put<Vessel>(`admin-center/clients/${clientId}/tenants/${tenantAlias}/vessels/${id}`, payload)
        .then(({ data }) => data);

export const deleteApiVessel = (clientId: number, tenantAlias: string, id: number): Promise<Vessel> =>
    httpApi
        .delete<Vessel>(`admin-center/clients/${clientId}/tenants/${tenantAlias}/vessels/${id}`)
        .then(({ data }) => data);

export const uploadApiVesselsCsv = (clientId: number, tenantAlias: string, formData: FormData): Promise<string> =>
    httpApi
        .post(`admin-center/clients/${clientId}/tenants/${tenantAlias}/vessels/upload-csv`, formData, {
            headers: CONTENT_TYPE_FORM_DATA_HEADER,
            timeout: NO_REQUEST_TIMEOUT,
        })
        .then(({ data }) => data);

export const getApiVesselsCsvUpload = (
    clientId: number,
    tenantAlias: string,
    id: string,
): Promise<CsvUploadStateResponse> =>
    httpApi
        .get<CsvUploadStateResponse>(`admin-center/clients/${clientId}/tenants/${tenantAlias}/vessels/upload-csv/${id}`)
        .then(({ data }) => data);

export const downloadApiVesselsCsv = (clientId: number, tenantAlias: string): Promise<AxiosResponse<Blob>> =>
    httpApi
        .get(`admin-center/clients/${clientId}/tenants/${tenantAlias}/vessels/download-csv`, { responseType: 'blob' })
        .then((res) => res);

export const downloadApiVesselsExampleCsv = (clientId: number, tenantAlias: string): Promise<AxiosResponse<Blob>> =>
    httpApi
        .get(`admin-center/clients/${clientId}/tenants/${tenantAlias}/vessels/example-csv`, { responseType: 'blob' })
        .then((res) => res);
